import { computed, action, observable, reaction } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { emit, events, on } from '@estee/elc-service-bus';

import * as queryParamUtils from '~utils/QueryParams';
import { ProductGridSettings } from '~domain/entities/ProductGridSettings';
import { GridItem } from '~domain/entities/GridItem';
import { Product } from '~domain/entities/Product';
import { contentSize, gridItemType } from '~constants/ProductGrid';
import {
    IContentItem,
    IProductGridWrapper
} from '../../exported/product-grid-wrapper/ProductGridWrapper';
import { FilterSet } from '~domain/entities/FilterSet';
import { Filter } from '~domain/entities/Filter';
import { IFilterSets } from '../views/product-filters/IProductFilters';

import { ProductGridService } from '~domain/services/ProductGridService';
import { mergeConfigs } from '~utils/ConfigUtils';
import { FilterService } from '~domain/services/FilterService';
import { IntersectionObserverEntity } from '~domain/entities/IntersectionObserver';

export enum FILTER_METHODS {
    filter = 'filter',
    sort = 'sort'
}

export interface IProductGridViewControllerData {
    gridItems: GridItem[];
    products: Product[];
    productsNumber: number;
    featuredProductId?: string;
    productSortingDropdownValue: string;
    isFilterDisplayTabbed: boolean;
    isFilterDisplayStacked: boolean;
    isAllGiftsFilterApplied: boolean;
    areFiltersApplied: boolean;
    categoryTag: string;
    appliedFilters: FilterSet[];
    scrollTopOffset: number;
}
export interface IAnalyticsAppliedFilters {
    option: string;
    category: string;
}

interface IFavorites {
    skuBaseIds: number[];
}

interface IFavoriteUpdatedEventData {
    skuBaseId: number;
    isFavorite: boolean;
    isAlreadyAdded?: boolean;
}
export class ProductGridViewController extends ViewController {
    public dataState: IProductGridViewControllerData;
    public configMergeStrategy = mergeConfigs;
    @observable public isLoaded: boolean = false;
    @observable public positionGridInsideAccordion: boolean = false;
    @observable public productGridService: ProductGridService;

    @observable private productGridSettings: ProductGridSettings;
    @observable private featuredProductId?: string;
    @observable private filtersData: FilterSet[] = [];
    @observable private analyticsAppliedFilters: IAnalyticsAppliedFilters[];

    @observable public products: Product[];
    @observable public filteredProducts: Product[];
    @observable private stickyElements: IntersectionObserverEntity[] = [];

    constructor(productGridSettings: ProductGridSettings, productGridService: ProductGridService) {
        super();

        this.productGridSettings = productGridSettings;
        this.productGridService = productGridService;

        reaction(
            () => this.extractedAppliedFilters,
            (appliedFilters) => {
                if (this.appliedFilters.length) {
                    this.analyticsAppliedFilters =
                        FilterService.getAnalyticsAppliedFilters(appliedFilters);

                    emit(events.GRID_FILTERED, {
                        appliedFilters: this.analyticsAppliedFilters
                    });
                }
            }
        );
        reaction(
            () => this.appliedFilters,
            () => {
                this.updateQueryString();
                this.setFilteredProducts();
                this.toggleApplicableFilters();
            },
            // this prevents the app from wiping out the query string on initial load
            {
                equals: (previousAppliedFilters, currentAppliedFilters) => {
                    return (
                        previousAppliedFilters.length === 0 && currentAppliedFilters.length === 0
                    );
                }
            }
        );
    }

    @action
    public loaded = async (productGridProps: IProductGridWrapper) => {
        const { filterData, featuredProductId, inventoryStatusSorting, backorder } =
            productGridProps.config;

        const productGridDataParams = this.productGridService.getProductGridDataParams(
            productGridProps.config
        );
        this.products = (await this.productGridService.getProductData(
            productGridDataParams.options,
            productGridDataParams.queryFragments
        )) as Product[];

        // Load grid cards based on the accessor
        this.featuredProductId = featuredProductId;

        if (inventoryStatusSorting && inventoryStatusSorting.enabled && backorder) {
            this.productGridSettings.setSortByInventoryStatus(
                inventoryStatusSorting.statuses || [],
                backorder.enabled
            );
        }

        const productIds = this.products.map((product) => product.productId);

        if (productIds.length > 0) {
            emit(events.PRODUCT_LOADED, productIds);
            emit(events.PRODUCT_GRID_VIEWED, { products: this.products });
        }

        // Load filtersets last
        if (filterData) {
            this.generateFilterSets(filterData.filterSets);
        }

        if (productGridProps.config.gridActionsBar?.stickyElementsSelectors) {
            this.initStickyElementsObservers(
                productGridProps.config.gridActionsBar.stickyElementsSelectors
            );
        }

        this.isLoaded = true;
    };

    @computed
    get finalProducts() {
        if (!this.appliedFilters.length) {
            return this.sortedProducts;
        }

        return this.filteredProducts;
    }

    @computed
    get data() {
        return {
            gridItems: this.gridItems,
            products: this.finalProducts,
            productsNumber: this.productsNumber,
            featuredProductId: this.featuredProductId,
            productSortingDropdownValue: this.productGridSettings.sortValue,
            isFilterDisplayTabbed: true,
            isFilterDisplayStacked: false,
            isAllGiftsFilterApplied: false,
            areFiltersApplied: this.areFiltersApplied,
            appliedFilters: this.appliedFilters,
            categoryTag: this.categoryTag,
            scrollTopOffset: this.stickyElementsHeight
        };
    }

    @computed
    public get categoryTag(): string {
        const defaultCategoryProduct = this.products.find((product: Product) =>
            Boolean(product.defaultCategory.value)
        );

        return (defaultCategoryProduct && defaultCategoryProduct.defaultCategory.value) || '';
    }

    @computed public get appliedFilters(): FilterSet[] {
        return this.filterService.getAppliedFilterSets(this.filtersData);
    }

    @computed public get totalAppliedFilters(): number {
        return this.appliedFilters.reduce((total, filterSet) => {
            return total + filterSet.filters.filter((filter) => filter.active).length;
        }, 0);
    }

    @computed public get extractedAppliedFilters(): Filter[] {
        return this.filterService.getAppliedFilters(this.appliedFilters);
    }

    @computed public get filters() {
        if (!this.filtersData) {
            return [];
        }

        return this.filtersData;
    }

    @computed
    public get gridItems(): GridItem[] {
        return this.insertContent(this.finalProducts, this.productGridService.inlineContent);
    }

    @computed
    private get areFiltersApplied() {
        return this.appliedFilters.length > 0;
    }

    @computed
    private get stickyElementsHeight() {
        return this.stickyElements.reduce((total: number, element: IntersectionObserverEntity) => {
            return total + element.elementHeight;
        }, 0);
    }

    @action
    private initStickyElementsObservers(stickyElementsSelectors: string[]) {
        if (stickyElementsSelectors.length) {
            this.stickyElements = stickyElementsSelectors.map((selector) => {
                return new IntersectionObserverEntity(selector);
            });
        }
    }

    // Checks URL for filter params and applies them to the Filter Set
    private applyUrlFilters = (): void => {
        const search = queryParamUtils.getQueryParams();
        const urlParams = new URLSearchParams(search);

        if (search.length > 0 && this.filtersData && this.filtersData.length) {
            // iterate over available filter sets
            this.filtersData.forEach((fSet) => {
                const convertedKey = this.filterService.convertFilterKeyLabel(fSet.key);
                // compare available filter keys to URL params
                if (urlParams.has(convertedKey)) {
                    // if a key match is found, get the value of the URL param
                    const queryVal = urlParams.get(convertedKey);
                    if (queryVal) {
                        // handle case of multiple comma-delimited values
                        const queryVals: string[] = queryVal.split(',');
                        // find the filter object that matches the URL param key & values
                        queryVals.forEach((val) => {
                            const urlFilter = fSet.filters.find(
                                (f) =>
                                    this.filterService.convertFilterValueLabel(f.label) ===
                                    this.filterService.convertFilterValueLabel(val)
                            );
                            if (urlFilter) {
                                // set that filter object to active
                                urlFilter.setActiveState(true);
                            }
                        });
                    }
                }
            });
        }
    };

    // update the Query String to reflect the updated filter state
    private updateQueryString = (): void => {
        const newUrl = this.filterService.getAppliedFiltersQueryString(
            this.appliedFilters,
            this.filters,
            window.location.search
        );
        window.history.replaceState({}, '', newUrl);
    };

    public insertContent = (productsArray: Product[], content: IContentItem[] = []) => {
        let positionIndex = 0;
        const arrCopy = productsArray.map((product: Product, index) => {
            positionIndex = index + 1;
            if (!this.areFiltersApplied && product.productId === this.featuredProductId) {
                return new GridItem(
                    gridItemType.featuredProduct,
                    this.featuredProductId,
                    positionIndex,
                    {
                        nId: this.featuredProductId,
                        nPsn: 1,
                        nSize: contentSize.large
                    },
                    product
                );
            }

            return new GridItem(
                gridItemType.product,
                product.productId,
                positionIndex,
                {},
                product
            );
        });

        if (arrCopy && arrCopy.length > 0 && content) {
            content.map((contentItem: IContentItem) => {
                positionIndex += 1;
                arrCopy.splice(
                    contentItem.nPsn - 1,
                    0,
                    new GridItem(
                        gridItemType.tout,
                        contentItem.nId.toString(),
                        positionIndex,
                        contentItem
                    )
                );
            });
        }

        return arrCopy;
    };

    public get filterService() {
        return this.productGridService.filterService;
    }

    public get productSortingService() {
        return this.productGridService.productSortingService;
    }

    @action
    public toggleApplicableFilters = () => {
        this.filtersData = this.filterService.getApplicableFilters(this.filtersData, this.products);
    };

    @action
    public setFilteredProducts = () => {
        if (!this.appliedFilters.length) {
            this.products = this.products.map((product) => {
                product.setFilteredSkus([]);

                return product;
            });
        } else {
            const filterProducts = this.filterService.filter(
                this.sortedProducts,
                this.appliedFilters
            );

            this.filteredProducts = filterProducts.reduce(
                (accumulator: Product[], product: Product) => {
                    const skuLevelFilters = this.appliedFilters.filter(
                        (filterSet) => filterSet.hasSkuFilters
                    );
                    const filteredSkus = this.filterService.filter(
                        product.initialSkus,
                        skuLevelFilters
                    );

                    if (filteredSkus && filteredSkus.length > 0) {
                        product.setFilteredSkus(filteredSkus);
                        accumulator.push(product);
                    }

                    return accumulator;
                },
                []
            );
        }
    };

    @computed
    private get sortedProducts() {
        return this.productSortingService.sortProducts(this.products, this.featuredProductId);
    }

    @computed
    public get productsNumber() {
        return this.finalProducts.length;
    }

    public setProductSortingDropdownValue = async (_: string, value: string) => {
        this.productGridSettings.setSortCriteria(value);
        emit(events.GRID_SORTED, {
            appliedSort: value
        });

        this.setFilteredProducts();
    };

    @action
    private generateFilterSets = (filterSetsJson: IFilterSets[]) => {
        this.filtersData = this.filterService.generateFilterSets(filterSetsJson, this.products);

        // add URL params to Filter Sets
        this.applyUrlFilters();
    };

    @action
    public setGridPosition = () => {
        this.positionGridInsideAccordion = true;
    };

    @action
    public updateFavorites = (favoriteSkuBaseIds: number[], isFavorite: boolean = true) => {
        this.products = this.products?.map((product) => {
            product.initialSkus = product.skus.map((sku) => {
                const {
                    perlgem: { SKU_BASE_ID: skuBaseId }
                } = sku;
                if (favoriteSkuBaseIds.includes(skuBaseId)) {
                    sku.isFavorite = isFavorite;
                }

                return sku;
            });

            return product;
        });
    };
    @on(events.FAVORITES_LOADED)
    public initFavorites = ({ skuBaseIds }: IFavorites) => {
        this.updateFavorites(skuBaseIds);
    };

    @on(events.FAVORITE_STATUS_UPDATED)
    public updateFavorite = ({
        skuBaseId,
        isFavorite,
        isAlreadyAdded
    }: IFavoriteUpdatedEventData) => {
        if (!isAlreadyAdded) {
            this.updateFavorites([skuBaseId], isFavorite);
        }
    };
}
